import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import io from "socket.io-client";
import SpectatorSpinWheel from "./SpectatorSpinWheel";
import SpectatorQuiz from "./SpectatorQuiz";
import SpectatorBuzzer from "./SpectatorBuzzer";
import SpectatorHigherOrLower from "./SpectatorHigherOrLower";
import SpectatorIdle from "./SpectatorIdle";
import BeerillionerScreen from "../beerillioner/BeerillionerScreen";
import { bindGameResync } from "../../../utils/bindGameResync";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const SpectatorPage = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);

  const fetchLive = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/spectator/live`);
      if (!res.ok) throw new Error("Failed to fetch spectator state");
      const data = await res.json();
      setState(data);
      setError(null);
    } catch (err) {
      console.error("[Spectator] fetch error:", err);
      setError("Unable to connect. Retrying...");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLive();
  }, [fetchLive]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("spectator:join");
      fetchLive();
    });
    const unbindResync = bindGameResync(() => {
      if (socket.connected) socket.emit("spectator:join");
      fetchLive();
    });

    socket.on("spectator:updated", (payload) => {
      setState(payload);
    });

    socket.on("disconnect", () => {
      setTimeout(() => {
        if (socketRef.current && !socketRef.current.connected) {
          fetchLive();
        }
      }, 3000);
    });

    return () => {
      unbindResync();
      if (socket) {
        socket.emit("spectator:leave");
        socket.disconnect();
      }
    };
  }, [fetchLive]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#0a0a0a" }}>
        <CircularProgress sx={{ color: "#9C27B0" }} />
      </Box>
    );
  }

  if (error && !state) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#0a0a0a" }}>
        <Typography sx={{ color: "#ff5252" }}>{error}</Typography>
      </Box>
    );
  }

  if (!state || state.status === "idle") {
    return <SpectatorIdle />;
  }

  const { game_type, session_id, config } = state;

  switch (game_type) {
    case "spinning_wheel":
      return <SpectatorSpinWheel sessionId={session_id} config={config} />;
    case "insta_quiz":
      return <SpectatorQuiz broadcastSessionId={session_id} />;
    case "beerillioner":
      return <BeerillionerScreen spectatorMode />;
    case "buzzer":
      return <SpectatorBuzzer />;
    case "higher_or_lower":
      return <SpectatorHigherOrLower sessionId={session_id} config={config} />;
    default:
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#0a0a0a" }}>
          <Typography sx={{ color: "#aaa", fontSize: "1.2rem" }}>
            Unsupported game type: {game_type}
          </Typography>
        </Box>
      );
  }
};

export default SpectatorPage;
